import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  KeyIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { formatDateTime } from "../../../utils/formatDate";
import { focusRing } from "../../Exams/TestBuilder/ui";
import { formatExactTime, formatSize, personName } from "./printText";
import { Badge, BusyButton, QuietButton } from "./ui";

const KINDS = ["student", "key"];

// The booklet's documents as the page shows them: `current`, its variants' own; `retired`, current on the server but
// of a variant digit taken out of the booklet since (the server replaces a variant's documents only when it prints
// that variant again), marked `retired`; and `superseded`. `variantNumbers`: the booklet's digits.
export const splitDocuments = (documents, variantNumbers) => {
  const numbers = Array.isArray(variantNumbers) ? new Set(variantNumbers) : null;
  const split = { current: [], retired: [], superseded: [] };
  (documents || []).forEach((document) => {
    if (document.superseded) split.superseded.push(document);
    else if (!numbers || numbers.has(document.number)) split.current.push(document);
    else split.retired.push({ ...document, retired: true });
  });
  return split;
};

// "Variant 9104 · Answer key · Preview": a document named in a dialog or a button's label. An old one (replaced, or of
// a variant taken out of the booklet) also says when it was made, to the second, and why it is old: that tells its
// versions apart.
export const documentLabel = (t, document, language) =>
  [
    t("bts.print.variant", { code: document.code }),
    t(`bts.print.kind_${document.kind === "key" ? "key" : "student"}`),
    document.preview ? t("bts.print.preview_badge") : null,
    document.retired || document.superseded
      ? t(document.retired ? "bts.print.version_retired" : "bts.print.version_replaced", {
          created: formatExactTime(document.created, language),
        })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

// The current documents by variant, in the order of the variants: [{number, code, student, key}].
const byVariant = (documents) => {
  const variants = new Map();
  documents.forEach((document) => {
    if (!variants.has(document.number)) variants.set(document.number, { number: document.number, code: document.code });
    const variant = variants.get(document.number);
    if (!variant[document.kind]) variant[document.kind] = document;
  });
  return [...variants.values()].sort((a, b) => a.number - b.number);
};

// By variant, the student booklet first.
const byNumberAndKind = (a, b) => a.number - b.number || KINDS.indexOf(a.kind) - KINDS.indexOf(b.kind);

// One document: what it is, its pages, size, when and by whom it was made, its downloads; "Download" and "Log".
// `old`: shown among the documents no longer current (with its variant, preview mark and why it is old).
function DocumentItem({ document, old, downloading, onDownload, onShowLog }) {
  const [t, i18n] = useTranslation();
  const key = document.kind === "key";
  const KindIcon = key ? KeyIcon : DocumentTextIcon;
  const author = personName(document.created_by);
  const label = documentLabel(t, document, i18n.language);
  const date = (value) => formatDateTime(value, i18n.language);
  const made = author ? t("bts.print.created_by", { date: date(document.created), name: author }) : t("bts.print.created", { date: date(document.created) });
  const downloads =
    document.download_count > 0
      ? [
          t("bts.print.downloads", { count: document.download_count }),
          document.last_downloaded ? t("bts.print.last_download", { date: date(document.last_downloaded) }) : null,
        ]
          .filter(Boolean)
          .join(", ")
      : t("bts.print.downloads_none");
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
      <div className="flex min-w-0 flex-[1_1_16rem] items-start gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ${
            key ? "bg-amber-50 text-amber-700 ring-amber-600/20" : "bg-blue-50 text-blue-700 ring-blue-600/10"
          }`}
          aria-hidden="true"
        >
          <KindIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-slate-900">
            {old && <span className="tabular-nums">{t("bts.print.variant", { code: document.code })} ·</span>}
            {t(`bts.print.kind_${key ? "key" : "student"}`)}
            {key && (
              <Badge tone="amber" Icon={LockClosedIcon}>
                {t("bts.print.confidential")}
              </Badge>
            )}
            {old && document.preview && <Badge tone="violet">{t("bts.print.preview_badge")}</Badge>}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {[document.page_count ? t("bts.print.pages", { count: document.page_count }) : null, formatSize(t, document.size, i18n.language), made]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {old && document.retired && <p className="mt-0.5 text-xs text-slate-500">{t("bts.print.retired_note")}</p>}
          {old && !document.retired && document.superseded_at && (
            <p className="mt-0.5 text-xs text-slate-500">{t("bts.print.superseded_at", { date: date(document.superseded_at) })}</p>
          )}
          <p className={`mt-0.5 text-xs ${document.download_count > 0 ? "text-slate-600" : "text-slate-500"}`}>{downloads}</p>
        </div>
      </div>
      {/* The download framed (the rest of the row on phones), its log quiet after it: the order on the screen is the
          order of the keyboard and of a screen reader, on every width. */}
      <div className="flex shrink-0 items-center gap-2 max-sm:w-full">
        <BusyButton
          type="button"
          color="secondary-outline"
          Icon={ArrowDownTrayIcon}
          text={t("buttons.download")}
          aria-label={t("bts.print.download_aria", { name: label })}
          onClick={() => onDownload(document)}
          busy={downloading.has(document.uuid)}
          className="px-4 max-sm:h-11 max-sm:flex-1"
        />
        <QuietButton
          Icon={ClockIcon}
          text={t("bts.print.log_button")}
          aria-label={t("bts.print.log_aria", { name: label })}
          onClick={() => onShowLog(document)}
        />
      </div>
    </div>
  );
}

/**
 * A booklet's documents, as splitDocuments() gives them: the current ones by variant (its student booklet and key
 * side by side when there is room, with the preview and out-of-date marks), and the old ones (retired, then
 * superseded) folded away. `downloading`: the uuids of the documents being downloaded. `onDownload(document)` (the
 * page asks before a key), `onShowLog(document)`.
 */
export default function DocumentList({ current, retired, superseded, downloading, onDownload, onShowLog }) {
  const [t] = useTranslation();
  // The retired first (current until their variant left the booklet), then the newest replaced (a print replaces
  // its documents at once).
  const old = [
    ...[...retired].sort(byNumberAndKind),
    ...[...superseded].sort(
      (a, b) => String(b.superseded_at || "").localeCompare(String(a.superseded_at || "")) || byNumberAndKind(a, b)
    ),
  ];
  const itemProps = { downloading, onDownload, onShowLog };

  return (
    <div className="space-y-3">
      {current.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
          {old.length ? t("bts.print.empty_superseded") : t("bts.print.empty")}
        </p>
      ) : (
        <ul className="space-y-3">
          {byVariant(current).map((variant) => {
            const preview = KINDS.some((kind) => variant[kind]?.preview);
            const stale = KINDS.some((kind) => variant[kind]?.stale);
            return (
              <li key={variant.number} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 bg-slate-50/70 px-4 py-2.5">
                  <h4 className="text-sm font-semibold tabular-nums text-slate-900">{t("bts.print.variant", { code: variant.code })}</h4>
                  {preview && (
                    <Badge tone="violet" Icon={EyeIcon}>
                      {t("bts.print.preview_badge")}
                    </Badge>
                  )}
                  {stale && (
                    <Badge tone="red" Icon={ExclamationTriangleIcon}>
                      {t("bts.print.stale_badge")}
                    </Badge>
                  )}
                  {preview && <span className="text-xs text-slate-500">{t("bts.print.preview_hint")}</span>}
                </div>
                {/* Side by side once each has 26rem; the 1px gaps draw the lines between them. */}
                <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,26rem),1fr))] gap-px bg-slate-100">
                  {KINDS.filter((kind) => variant[kind]).map((kind) => (
                    <li key={kind} className="bg-white p-4">
                      <DocumentItem document={variant[kind]} {...itemProps} />
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}

      {old.length > 0 && (
        <details className="group rounded-xl border border-slate-200 bg-slate-50/60">
          <summary
            className={`flex min-h-[2.75rem] cursor-pointer list-none items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100/70 [&::-webkit-details-marker]:hidden ${focusRing}`}
          >
            <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-90" aria-hidden="true" />
            {t("bts.print.superseded_title", { n: old.length })}
          </summary>
          <div className="border-t border-slate-200 px-4 pb-4 pt-3">
            <p className="mb-3 text-xs text-slate-500">{t("bts.print.superseded_hint")}</p>
            <ul className="divide-y divide-slate-200/70">
              {old.map((document) => (
                <li key={document.uuid} className="py-3 first:pt-0 last:pb-0">
                  <DocumentItem document={document} old {...itemProps} />
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}
